// Centralized content for BestBuyIncentives.com

export const NAV_LINKS = [
  { label: "Incentives", to: "/programs" },
  { label: "Industries", to: "/industries" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Resources", to: "/resources" },
  { label: "Sales Resources", to: "/articles" },
  { label: "FAQ", to: "/faq" },
  { label: "About", to: "/about" },
];

export const STATS = [
  { value: "Since 1992", label: "Trusted by sales teams" },
  { value: "37%", label: "Avg. close-rate lift" },
  { value: "1,200+", label: "Sales teams deployed" },
  { value: "$184M", label: "Added pipeline closed" },
  { value: "4.8x", label: "ROI vs. discount promos" },
  { value: "92%", label: "Reps actively use the tool" },
  { value: "<24h", label: "Certificates in your reps' hands" },
];

export const PROBLEM_POINTS = [
  {
    title: "Margin Erosion",
    body: "Every discount you offer comes straight off your bottom line. A 10% price cut on a 30% margin product cuts your profit by a third.",
  },
  {
    title: "Promotion Fatigue",
    body: "Customers stopped responding to your last three campaigns. The market is numb to 'sales' and 'limited-time' events.",
  },
  {
    title: "Sameness",
    body: "Your competitors are doing exactly what you're doing. Price, financing, free delivery — nobody's winning, everyone's bleeding.",
  },
  {
    title: "Stalled Closes",
    body: "Reps need a reason to push for the close today. Without something memorable to offer, deals slide into next quarter.",
  },
];

export const HOW_IT_WORKS = [
  {
    n: "01",
    title: "Choose your program",
    body: "Pick from premium getaway certificates, cruise programs, or fully custom incentives sized to your deal value.",
  },
  {
    n: "02",
    title: "Equip your sales team",
    body: "Reps get a tangible, high-perceived-value tool to break stalls, justify price, and close on the first call.",
  },
  {
    n: "03",
    title: "Customers redeem with us",
    body: "We handle every redemption end-to-end — your team never touches travel logistics.",
  },
  {
    n: "04",
    title: "You measure the lift",
    body: "Close rate, average order value, and showroom traffic all move. Most clients see ROI inside 60 days.",
  },
];

export const BUSINESS_TESTIMONIALS = [
  {
    quote:
      "We've run promotions for decades. The discounted travel voucher promotion outperformed every other promotion we tried — and it kept working year after year.",
    author: "Bricker-Tunis",
    role: "Owner, Bricker-Tunis",
    metric: "Decades of repeat purchase",
  },
  {
    quote:
      "Once we started including the discounted travel voucher with every qualifying purchase, the phones rang off the hook — our showroom hadn't been that busy in five years.",
    author: "Regional Furniture Retailer",
    role: "General Manager",
    metric: "5-year traffic high",
  },
  {
    quote:
      "We had the single biggest sales day in dealership history when we ran the program. Customers literally asked when we'd run it again.",
    author: "Multi-Location Auto Dealer",
    role: "Sales Director",
    metric: "Record-breaking sales day",
  },
  {
    quote:
      "I stopped discounting. I started giving away vacations. My average ticket went up and my close rate went up. I will never go back.",
    author: "Luxury Jewelry Store",
    role: "Owner",
    metric: "+22% avg. ticket",
  },
];

export const CONSUMER_TESTIMONIALS = [
  {
    quote:
      "We took the kids to Punta Cana. Five days at an all-inclusive — I cannot believe this came as a thank-you from buying a couch.",
    author: "Jenna & Mark",
    location: "Punta Cana, Dominican Republic",
    image: "/images/testimonial-punta-cana.png",
  },
  {
    quote:
      "New Orleans for our anniversary. We told every couple at dinner where this came from. Best buying decision we ever made.",
    author: "Tom & Lisa",
    location: "New Orleans, LA",
    image: "/images/testimonial-new-orleans.png",
  },
  {
    quote:
      "A private villa in Greece. I keep telling people I bought a car and got a vacation. They don't believe me.",
    author: "Aaron K.",
    location: "Santorini, Greece",
    image: "/images/testimonial-greece.png",
  },
];

export const PROGRAMS = [
  {
    slug: "enjoy-a-great-escape",
    name: "Enjoy a Great Escape",
    tagline: "The closing weapon",
    description:
      "Our flagship travel-incentive certificate — a multi-night domestic & international getaway included with the sale. High perceived value, issued on demand, and proven to break stalls and drive net-new showroom traffic.",
    bullets: [
      "Multi-night getaway package",
      "Issued on demand — no inventory to stock",
      "Co-branded marketing collateral included",
      "Bulk pricing sized to your ticket",
    ],
    image: "/images/luxury-getaway-ad.png",
    altImage: "/images/cert-escape-v2.jpg",
    badge: "Best for: high-ticket sales floors",
  },
];

export const INDUSTRIES = [
  {
    name: "Auto Dealerships",
    desc: "Move slow inventory. Win the price-shopper. Reward your highest-ticket buyers and reduce buyer's remorse on the lot.",
    metric: "+38% close on test-driven units",
  },
  {
    name: "Jewelry Stores",
    desc: "Engagement season, holiday, and Mother's Day campaigns that don't depend on discounting your margins.",
    metric: "+22% average ticket",
  },
  {
    name: "Furniture",
    desc: "Hundreds of certificates per quarter is standard. Showroom traffic responds within 48 hours of campaign launch.",
    metric: "5-year traffic highs",
  },
  {
    name: "Home Improvement",
    desc: "High-ticket projects (windows, roofing, solar, kitchens) where the close window is short and the competition is fierce.",
    metric: "+31% in-home close rate",
  },
  {
    name: "E-Commerce Retailers",
    desc: "Lift average order value and rescue abandoned carts. A getaway with the purchase turns one-time buyers into repeat customers.",
    metric: "+29% repeat purchase",
  },
  {
    name: "Travel Clubs",
    desc: "Sell more memberships and renewals. A premium incentive gives prospects the push to join today and members a reason to stay.",
    metric: "+33% membership sales",
  },
  {
    name: "Health Clubs",
    desc: "Close membership tours on the spot and cut cancellations. A vacation reward beats another free month on price and appeal.",
    metric: "+26% membership close",
  },
  {
    name: "Employee Rewards",
    desc: "Motivate performance and keep top talent. A dream getaway outshines a gift card and builds loyalty money can't buy.",
    metric: "2.1x engagement",
  },
  {
    name: "Luxury Retail",
    desc: "Reward your VIP clients. Generate referrals. Reinforce the premium relationship between you and your best buyers.",
    metric: "VIP referrals 2.4x",
  },
  {
    name: "B2B Sales Orgs",
    desc: "Sales teams need credible ways to create urgency and add value. Discounted travel vouchers can support the close when the buyer, offer, and program fit.",
    metric: "+44% meeting acceptance",
  },
];

export const CASE_STUDIES = [
  {
    slug: "midwest-furniture-chain",
    company: "Midwest Furniture Chain (8 stores)",
    industry: "Furniture",
    challenge: "Memorial Day promo fatigue — discounts no longer moving traffic.",
    solution: "Deployed 800 'Enjoy a Great Escape' certificates across 8 stores for a 21-day campaign.",
    results: [
      { k: "Showroom traffic", v: "+58%" },
      { k: "Close rate", v: "+24%" },
      { k: "Avg. order value", v: "+12%" },
      { k: "ROI vs. discount promo", v: "5.2x" },
    ],
    image: "/images/case-studies/furniture-store.png",
  },
  {
    slug: "luxury-auto-dealer",
    company: "Luxury Auto Dealer (Top 100 nationally)",
    industry: "Auto",
    challenge: "High buyer's remorse on $80K+ vehicles, leading to lost referrals.",
    solution: "Bundled 'Ultimate 7-Night Cruise' with every $75K+ delivery as a thank-you reward.",
    results: [
      { k: "Buyer's remorse calls", v: "−63%" },
      { k: "Referrals per delivery", v: "+2.1x" },
      { k: "Repeat buyer rate (24mo)", v: "+18%" },
      { k: "CSI score", v: "98.7" },
    ],
    image: "/images/case-studies/luxury-auto-dealer.png",
  },
  {
    slug: "regional-jeweler",
    company: "Regional Jewelry Chain (12 locations)",
    industry: "Jewelry",
    challenge: "Engagement-season margin pressure from online discounters.",
    solution: "Every $3K+ purchase included a 'Be Our Guest' getaway. No price match needed.",
    results: [
      { k: "Average ticket", v: "+22%" },
      { k: "Online-comparison loss rate", v: "−41%" },
      { k: "Walk-out rate", v: "−19%" },
      { k: "Net new social mentions", v: "3,200+" },
    ],
    image: "/images/case-studies/jewelry-store.png",
  },
];

export const FAQS = [
  {
    q: "How is this different from a gift card?",
    a: "Gift cards have a known dollar value. Vacations carry a perceived value far higher than their cost. A $25K vacation memory closes deals a $200 gift card never will — and protects your margin.",
  },
  {
    q: "Do my reps need training to use this?",
    a: "Experienced sales teams may already know how to present a customer incentive. Either way, you'll receive guidance for explaining the discounted travel voucher accurately and using it at the appropriate point in the close.",
  },
  {
    q: "What does it cost us per certificate?",
    a: "Programs are priced for bulk deployment. Per-certificate cost is a small fraction of the deal it closes. Pricing depends on volume and program — request a custom quote.",
  },
  {
    q: "What's the customer's experience like?",
    a: "Customers book their vacation directly — you never handle anything. The travel support teams are highly trained, courteous, and go overboard to make sure every guest has an excellent experience.",
  },
  {
    q: "Can I use one of the certificates for myself?",
    a: "Absolutely, YES. Why not reward yourself with a vacation and enjoy all the same benefits that your customers are enjoying.",
  },
  {
    q: "How quickly can we launch?",
    a: "Most clients are live in 5–10 business days. Certificates are issued on demand — and you can lock in even deeper discounts with a monthly subscription.",
  },
  {
    q: "How does a business review a travel voucher program before running it?",
    a: "Review how the program works end to end: how certificates are issued, how your reps present them at the close, and how recipients redeem directly with the travel support team. A short consultation walks you through the mechanics and the program that fits your product and margin.",
  },
  {
    q: "Who handles the travel logistics for the business?",
    a: "A dedicated travel support team manages every booking, so your sales team never touches travel logistics. You issue the certificate at the close and stay focused on selling.",
  },
];

// Expanded FAQ for the /faq page — written in the natural-language phrasing
// buyers use when asking AI assistants (ChatGPT, Claude, Perplexity), so the
// answers map cleanly to real queries and get cited. Grouped by category.
export const FAQ_PAGE = [
  // The Basics
  {
    category: "The Basics",
    q: "What is BestBuyIncentives?",
    a: "BestBuyIncentives is a sales-growth company operated by Karl Kramer & Company, Inc., in business since 1992. It helps high-ticket sales teams use discounted travel vouchers as customer-facing closing tools that can add value without automatically cutting price.",
  },
  {
    category: "The Basics",
    q: "What is a travel incentive certificate?",
    a: "A travel incentive certificate is a vacation reward you give a customer as part of closing a sale. It carries a perceived value far higher than its cost to you, so it motivates a buying decision while protecting your margin. The customer redeems the trip directly through a dedicated travel support team — your reps never handle logistics.",
  },
  {
    category: "The Basics",
    q: "How do sales incentive programs work?",
    a: "You buy certificates in bulk, then your reps offer one to close or upsell a deal (for example, 'buy today and enjoy a complimentary getaway'). The customer accepts, you close at full price, and the customer books their vacation directly with the travel team. It replaces discounting with an experience buyers actually get excited about.",
  },
  {
    category: "The Basics",
    q: "How is this different from a gift card?",
    a: "Gift cards have a known, fixed dollar value that buyers mentally deduct from your price. A vacation carries a perceived value many times its cost, so a memorable trip closes deals a small gift card never will — and it protects your margin instead of eroding it.",
  },
  {
    category: "The Basics",
    q: "Is this the same as offering a discount?",
    a: "No — it's the opposite. Discounting comes straight off your gross margin and trains buyers to wait for the next price cut. A travel incentive adds perceived value on top of your offer, so you hold your price, protect profit, and still give the customer a reason to say yes now.",
  },

  // Results & ROI
  {
    category: "Results & ROI",
    q: "Can discounted travel vouchers help increase close rates?",
    a: "Yes. Teams using BestBuyIncentives report an average close-rate lift of about 37%. Instead of negotiating on price, reps hand the buyer a compelling reason to act, which shortens the sales cycle and increases the percentage of deals that close.",
  },
  {
    category: "Results & ROI",
    q: "How much can a travel incentive increase my sales?",
    a: "Results vary by team and offer, but clients typically see three things move at once: higher close rate (about 37% on average), higher average ticket, and more referrals. Because you stop discounting, that added revenue lands with your margin intact. Use the ROI calculator on our site to model your own numbers.",
  },
  {
    category: "Results & ROI",
    q: "How can discounted travel vouchers help protect profit margin?",
    a: "A 10% discount on a 30%-margin product wipes out roughly a third of your profit on that deal. A travel incentive costs you a small fraction of the deal it closes while the customer perceives it as high value — so you keep your price and your margin while still winning the sale.",
  },

  // Pricing & Terms
  {
    category: "Pricing & Terms",
    q: "How much does a discounted travel voucher cost?",
    a: "Certificates are priced for bulk deployment, so the per-certificate cost is a small fraction of the deal it helps close. Exact pricing depends on volume and program — book a quick call for a custom quote. A monthly subscription unlocks even deeper discounts.",
  },
  {
    category: "Pricing & Terms",
    q: "Is there a minimum order or subscription?",
    a: "Certificates can be issued on demand, and you can lock in deeper pricing with a monthly subscription. There's no complicated commitment to get started — we'll recommend the volume and structure that fits your sales floor on a short strategy call.",
  },
  {
    category: "Pricing & Terms",
    q: "Can I use a certificate for myself?",
    a: "Absolutely, yes. Reward yourself with a vacation and enjoy all the same benefits your customers do.",
  },

  // Trust & Legitimacy
  {
    category: "Trust & Legitimacy",
    q: "Are these travel incentives legitimate, or is it a scam?",
    a: "They're legitimate. BestBuyIncentives has operated since 1992 and has served 1,200+ sales teams. Resorts and travel providers offer these stays to fill availability and earn word-of-mouth exposure, and a trained travel support team handles every booking, so both you and your customers are taken care of.",
  },
  {
    category: "Trust & Legitimacy",
    q: "How are these travel offers even possible?",
    a: "Resorts want to fill unsold room nights and love the organic exposure guests create by sharing their trips. That makes offering discounted or complimentary stays a win for the resort, a win for your customer, and a win for you as the business giving the reward.",
  },
  {
    category: "Trust & Legitimacy",
    q: "How long has BestBuyIncentives been in business?",
    a: "Since 1992. It's operated by Karl Kramer & Company, Inc. (Bellmore, NY) and has helped more than 1,200 sales teams close more business with incentives instead of discounts.",
  },

  // The Customer Experience
  {
    category: "The Customer Experience",
    q: "What is the customer's experience like?",
    a: "Customers book their vacation directly with a highly trained, courteous travel support team that goes out of its way to give every guest an excellent experience. You never handle the logistics — your only job is to hand over the certificate and close the deal.",
  },
  {
    category: "The Customer Experience",
    q: "How do customers use their discounted travel voucher?",
    a: "The customer follows the simple instructions on their certificate and books directly with the travel support team, choosing their dates and destination from the available options. Everything after you hand over the certificate is handled for them.",
  },
  {
    category: "The Customer Experience",
    q: "Do my reps need training to use this?",
    a: "Experienced representatives may already know how to present a customer incentive, and the program includes guidance so the team can explain the discounted travel voucher accurately.",
  },

  // Getting Started
  {
    category: "Getting Started",
    q: "What types of businesses use travel incentives?",
    a: "High-ticket and high-consideration sales teams — including auto dealerships, jewelers, furniture retailers, home-improvement companies, e-commerce retailers, travel clubs, health clubs, employee-rewards programs, luxury retail, and B2B sales organizations — where protecting margin and standing out beats competing on price.",
  },
  {
    category: "Getting Started",
    q: "How quickly can we launch a program?",
    a: "Most clients are live in 5–10 business days. Certificates are issued on demand, so you can put a closing tool on your sales floor fast.",
  },
  {
    category: "Getting Started",
    q: "How do I get started or get pricing?",
    a: "Book a free 30-minute strategy call. We'll look at your sales floor, recommend the right program and volume, and send a custom quote — no obligation.",
  },
];


export const RESOURCES = [
  {
    slug: "how-to-position-a-vacation-incentive-in-a-close",
    title: "How to Position a Discounted Travel Voucher in a Close",
    category: "Sales Tactics",
    minutes: 8,
    excerpt: "The four-line script your reps need. When to introduce it. How to use it for stall-breaking and same-day close.",
    image: "/images/vac-plane.jpg",
    body: [
      "Never lead with the vacation. Lead with the value of the purchase. Introduce the incentive only after the buyer has emotionally committed to the product.",
      "Use it to break a stall: 'We can't hold this price beyond today, but we can include the getaway if we sign by close of business.' That single line moves 22% of stalled deals to close on the same day in field tests.",
      "Use it to reduce remorse: hand the certificate at delivery, not at signing. Customers remember the moment forever.",
    ],
  },
];

export const VIDEO_PLACEHOLDERS = [
  {
    title: "How to Use a Discounted Travel Voucher in Your Close",
    duration: "08:24",
    label: "Sales Tactic — Coming Soon",
  },
  {
    title: "The Stall-Breaker Script (Verbatim)",
    duration: "06:11",
    label: "Closer's Playbook — Coming Soon",
  },
  {
    title: "Deploying the Certificate at Delivery",
    duration: "05:47",
    label: "Buyer's Remorse Prevention — Coming Soon",
  },
];

export const TRUST_LOGOS = [
  "MERIDIAN AUTO GROUP",
  "GRANT & SONS JEWELERS",
  "NORTHWAY FURNITURE",
  "APEX HOME EXTERIORS",
  "SUMMIT HEALTH CLUBS",
  "HARBOR TRAVEL CLUB",
  "WEST COAST LUXURY",
  "PINNACLE B2B",
];
